import React from 'react';
import { message, Popconfirm, Tooltip } from 'antd'
import { useAtom, useAtomValue } from 'jotai';
import { CloseOutlined } from '@ant-design/icons';
import { useQueryClient } from 'react-query';
import { CardinalityValue, CombinationText, CombinationValue, CombinationWrapper } from '../styled';
import { CombinationProp } from '../alertConfig.types';
import { getCardinalityText, getExtensionText, getHilightColor, getHilightCombinations, isFirstDimension, getDeletionCombination, getDeleteText, getDimensionNameList } from '../alertConfig.utils';
import { hilightDimensionAtom, isConfigLoadingAtom } from '@components/aiAlerts/atoms';
import { MAX_DIMENSION } from '../alertConfig.constants';
import { useUpdateConfigList } from '@components/metrics-panel-hz/hooks/alerts/useUpdateConfigList';
import { CONFIG_LIST_KEY } from '@components/metrics-panel-hz/hooks/alerts/alertsQueryKey.constants';

export default function Combination({ combinationSet, level }: CombinationProp) {
  const queryClient = useQueryClient()
  const { name, backendName, cardinality, isCustomDimension, isBeingAdded, isBeingDeleted } = combinationSet;
  const combinationList = getDimensionNameList(name);
  const [hilightDimensions, setHilightDimension] = useAtom(hilightDimensionAtom);
  const shallDelete = getDeletionCombination(hilightDimensions, backendName);
  const dimensionText = isFirstDimension(level) ? 'this dimension' : 'these dimensions';
  const queryData = queryClient.getQueryData(CONFIG_LIST_KEY);
  const aiConfigList = queryData?.aiAlertsConfig ?? [];
  const extensionText = getExtensionText(hilightDimensions);
  const isCardinalityFetching = useAtomValue(isConfigLoadingAtom);
  const isValidLevel = level < MAX_DIMENSION;
  const displayDeleteBtn = isCustomDimension && isValidLevel && !isBeingDeleted;
  const cardinalityHoverText = getDeleteText(isBeingAdded, isBeingDeleted);
  const popupText = `Are you sure to remove ${dimensionText}? This will affect the highlighted ${extensionText}.`;
  const hilightColor = getHilightColor(shallDelete, isBeingDeleted);

  const onDimensionRemoveSuccess = () => {
    message.open({
      content: `${extensionText} deleted`,
      type: 'success',
      style: { textTransform: 'capitalize' }
    });
  };

  const onDimensionRemoveError = () => {
    message.open({
      content: `unable to delete ${extensionText}`,
      type: 'error',
      style: { textTransform: 'capitalize' }
    });
  };

  const { mutate: mutateAiAlertsConfig } = useUpdateConfigList(onDimensionRemoveSuccess, onDimensionRemoveError);

  const onPopToggle = (open) => {
    if (open || !hilightDimensions.length) return;

    setHilightDimension([]);
  };

  const removeHilightedDimensions = () => {
    const aiAlertsConfig = aiConfigList?.map(levelData => {
      const { dimensionData = [] } = levelData;
      const updatedDimensionData = dimensionData.filter(({ backendName }) => {
        return !hilightDimensions.some(({ backendName: deletedBackendName }) => deletedBackendName === backendName);
      });

      return { ...levelData, dimensionData: updatedDimensionData };
    });

    mutateAiAlertsConfig({ aiAlertsConfig });
    setHilightDimension([]);
  }

  const onConfirm = () => {
    removeHilightedDimensions();
  };

  const checkSubsequentCombinations = (aiConfigList, combination, level, backendName) => {
    const hilightCombinations = getHilightCombinations(aiConfigList, combination, level, backendName);

    setHilightDimension(hilightCombinations);
  };

  return (
    <CombinationWrapper>
      {displayDeleteBtn &&
        <Popconfirm
          placement="top"
          title={popupText}
          style={{ width: 'max-content' }}
          onConfirm={onConfirm}
          okText="Delete"
          cancelText="No"
          onOpenChange={onPopToggle}
          getPopupContainer={trigger => trigger.parentElement}
          overlayStyle={{ width: 'max-content' }}
        >
          <CombinationText onClick={() => checkSubsequentCombinations(aiConfigList, combinationList, level, backendName)}>
            <CloseOutlined color="red" />
          </CombinationText>
        </Popconfirm>
      }
      <CombinationValue hilightDimension={hilightColor} isCustom={isCustomDimension} isBeingDeleted={isBeingDeleted} alignItems={combinationList.length > 1 ? 'flex-start' : 'center'} >
        {combinationList.map((combName, ind) => (<div key={ind}>{combName}</div>))}
      </CombinationValue>
      <Tooltip title={cardinalityHoverText}>
        <CardinalityValue hilightDimension={hilightColor} isBeingDeleted={isBeingDeleted}>
          {getCardinalityText(cardinality, level, isCardinalityFetching, false, isBeingAdded, isBeingDeleted)}
        </CardinalityValue>
      </Tooltip>
    </CombinationWrapper>
  )
}
